import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];
const SPEED_KEY = "podcast-playback-rate";

const formatTime = (sec) => {
  if (!Number.isFinite(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

export const PodcastPlayer = ({ src }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(() => {
    const saved = parseFloat(localStorage.getItem(SPEED_KEY) || "1");
    return SPEEDS.includes(saved) ? saved : 1;
  });

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.playbackRate = speed;
    // Zachovat přirozenou výšku hlasu při změně rychlosti
    if ("preservesPitch" in a) a.preservesPitch = true;
  }, [speed]);

  const changeSpeed = (s) => {
    setSpeed(s);
    localStorage.setItem(SPEED_KEY, String(s));
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play();
    } else {
      a.pause();
    }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !Number.isFinite(a.duration)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * a.duration;
  };

  const progress = duration > 0 ? (time / duration) * 100 : 0;

  return (
    <div
      className="border border-[#F0F0F0] bg-[#FAFAF8] p-4 md:p-5"
      data-testid="podcast-player-custom"
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        data-testid="podcast-audio-element"
      >
        Váš prohlížeč nepodporuje přehrávání audia.
      </audio>

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={toggle}
          data-testid="podcast-player-toggle"
          aria-label={playing ? "Pozastavit epizodu" : "Přehrát epizodu"}
          className="flex items-center justify-center w-12 h-12 rounded-full bg-[#B69A58] text-white shrink-0 hover:bg-[#a68c4e] transition-colors duration-300"
        >
          {playing ? (
            <Pause className="h-5 w-5" strokeWidth={1.5} fill="currentColor" />
          ) : (
            <Play className="h-5 w-5 ml-0.5" strokeWidth={1.5} fill="currentColor" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={seek}
            data-testid="podcast-player-seekbar"
            aria-label="Posunout přehrávání"
            className="relative block w-full h-6 group"
          >
            <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 rounded-full bg-[#E5E0D3] overflow-hidden">
              <span
                className="absolute inset-y-0 left-0 bg-[#B69A58] rounded-full"
                style={{ width: `${progress}%` }}
              />
            </span>
            <span
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-[#B69A58] shadow-sm transition-transform duration-150 group-hover:scale-110"
              style={{ left: `${progress}%` }}
            />
          </button>
          <div className="flex items-center justify-between mt-1 text-xs text-[#737373] tabular-nums">
            <span data-testid="podcast-player-current">{formatTime(time)}</span>
            <span data-testid="podcast-player-duration">{formatTime(duration)}</span>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-[#EFE7D6]">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#737373] font-medium mb-2">
          Rychlost přehrávání
        </p>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Rychlost přehrávání">
          {SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => changeSpeed(s)}
              data-testid={`podcast-speed-${String(s).replace(".", "-")}`}
              aria-pressed={speed === s}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
                speed === s
                  ? "bg-[#B69A58] border-[#B69A58] text-white"
                  : "bg-white border-[#E5E0D3] text-[#4A4A4A] hover:border-[#B69A58] hover:text-[#B69A58]"
              }`}
            >
              {s.toLocaleString("cs-CZ")}×
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
